import { Component, inject, Input, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Attachment, Patient } from 'fhir/r4';
import { MatTabsModule, MatTabGroup } from '@angular/material/tabs';
import { PatientDataReviewComponent, FieldEditEvent } from '../shared/patient-data-review/patient-data-review.component';
import { formMetaData } from '../shared/dynamic-forms.interface';
import { CodeableConceptField, CodeField, GroupField, IndividualField } from '../shared/dynamic-forms.interface2';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { FieldConfigFactoryService } from '../shared/field-config-factory.service';
import { AttachmentEditorComponent } from '../shared/attachment-editor/attachment-editor.component';
import { pruneFhirResource, sanitizePatientResource } from '../shared/patient-resource.utils';
import { Practitioner } from 'fhir/r4b';
import { patientSignatureExtensionUrlToken, practitionerSignatureExtensionUrlToken } from '../app.config';
import { SignatureCanvasComponent } from '../shared/signature-canvas/signature-canvas.component';
import { getSignatureAttachment, setSignatureAttachment } from '../shared/signature-extension.utils';

@Component({
    selector: 'app-patient-registration-edit',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule, MatTabsModule, PatientDataReviewComponent, AttachmentEditorComponent, SignatureCanvasComponent],
    template: `
    <div class="p-20" style="box-sizing: border-box;">
    <h2 mat-dialog-title>{{ dialogTitle }}</h2>
  <div class="edit-dialog">
    <div class="tab-scroller">
        <mat-tab-group #tabGroup>
        <mat-tab [label]="detailsTabLabel">
    <app-patient-data-review
      *ngIf="designatedFormFields && designatedSubmittedData"
      [formFields]="designatedFormFields"
      [submittedData]="designatedSubmittedData"
            (fieldEdited)="onDetailsEdited($event)"
                (navigateNext)="goNextTabOrSave()"
                (navigatePrev)="goPrevTab()"></app-patient-data-review>
    </mat-tab>
    <mat-tab label="Guardian" *ngIf="!isEditModeForPractitioner">
    <app-patient-data-review
      *ngIf="guardianFormFields && guardianSubmittedData"
      [formFields]="guardianFormFields"
      [submittedData]="guardianSubmittedData"
            (fieldEdited)="onGuardianEdited($event)"
                (navigateNext)="goNextTabOrSave()"
                (navigatePrev)="goPrevTab()"></app-patient-data-review>
    </mat-tab>
        <mat-tab [label]="attachmentsTabLabel">
            <app-attachment-editor
                [attachments]="attachmentsSubmittedData"
                [allowManual]="true"
                [allowUpload]="true"
                [allowedFiles]="attachmentAllowedFiles"
                [maxFiles]="5"
                (attachmentsChange)="onAttachmentsChanged($event)">
            </app-attachment-editor>
    </mat-tab>
        <mat-tab [label]="signatureTabLabel">
            <app-signature-canvas
                [initialAttachment]="signatureAttachment"
                (signatureChange)="onSignatureChanged($event)">
            </app-signature-canvas>
    </mat-tab>
    </mat-tab-group>
    </div>
  <div class="actions g-just-flex gap-10 justify-content-end mt-15">
    <button mat-stroked-button type="button" (click)="dialogRef.close()">Cancel</button>
    <button mat-flat-button color="primary" type="button" (click)="save()">Save</button>
  </div>
  </div>
  </div>
  `,
    styles: [`
    .edit-dialog{display: flex; flex-direction: column; box-sizing: border-box;}
    .tab-scroller{flex: 1 1 auto; overflow: auto; padding: 8px 4px 12px;}
    .actions{padding: 8px 0 4px; box-sizing: border-box;}
    @media (max-width: 768px){ .edit-dialog{min-width: auto; width: 92vw;} }
  `]
})
export class PatientRegistrationEditComponent {
    // Reference to the tab group for programmatic navigation
    @ViewChild('tabGroup') tabGroup?: MatTabGroup;
    dialogRef = inject(MatDialogRef<PatientRegistrationEditComponent>);
    private rawData = inject(MAT_DIALOG_DATA) as any;
    @Input() isEditModeForPractitioner = false;
    private hiddenPrimaryEmail: any = null;
    private hiddenPrimaryPhone: any = null;
    data: Patient | Practitioner;
    dialogTitle = ``
    detailsTabLabel = 'Patient Details';
    attachmentsTabLabel = 'Attachments';
    signatureTabLabel = 'Signature';
    dialogContext: 'profile' | 'patient' | 'practitioner' = 'patient';

    // Field configs
    designatedRegForm?: formMetaData;
    designatedFormFields?: (IndividualField | CodeField | CodeableConceptField | GroupField)[];
    guardianRegForm?: formMetaData;
    guardianFormFields?: (IndividualField | CodeField | CodeableConceptField | GroupField)[];

    // Submitted-like data mapped from FHIR
    designatedSubmittedData: any;
    guardianSubmittedData: any;
    attachmentsSubmittedData: Attachment[] = [];
    signatureAttachment: Attachment | null = null;

    // Reuse the same allowed files defaults as registration flows so uploads work in the edit dialog.
    attachmentAllowedFiles = /\.(jpg|jpeg|png|gif|pdf|docx|xlsx|txt)$/i;

    workingPatient: Patient;
    selectService = inject(FormFieldsSelectDataService);
    fieldFactory = inject(FieldConfigFactoryService);
    private readonly patientSignatureExtensionUrl = inject(patientSignatureExtensionUrlToken);
    private readonly practitionerSignatureExtensionUrl = inject(practitionerSignatureExtensionUrlToken);

    constructor() {
        const payload = this.rawData;
        if (payload && payload.resource) {
            this.data = payload.resource as Patient | Practitioner;
            this.isEditModeForPractitioner = payload.isEditModeForPractitioner ?? false;
            this.dialogContext = payload.context === 'profile' ? 'profile' : `${this.isEditModeForPractitioner ? 'practitioner' : 'patient'}`;

        } else {
            this.data = payload as Patient | Practitioner;
        }

        this.dialogTitle = this.dialogContext === 'profile' ? 'Edit Your Profile' : `Edit ${this.dialogContext === 'practitioner' ? 'Practitioner' : 'Patient'}`;
        this.detailsTabLabel = this.dialogContext === 'profile' ? 'Profile Details' : `${this.dialogContext === 'practitioner' ? 'Practitioner' : 'Patient'} Details`;
        this.attachmentsTabLabel = 'Photo';

        // Start from a clone to avoid mutating the input
        this.workingPatient = JSON.parse(JSON.stringify(this.data || { resourceType: 'Patient' }));
        // Strip any UI-only synthetic fields (like 'status') defensively
        delete (this.workingPatient as any).status;
    }

    ngOnInit() {
        // Build field configs similar to registration for Patient + Guardian
        this.prepareFieldConfigs().then(() => {
            // Map incoming FHIR Patient to submitted-like data shapes the review component expects
            this.designatedSubmittedData = this.mapPatientToDesignated(this.workingPatient);
            this.guardianSubmittedData = this.mapPatientToGuardian(this.workingPatient);
            this.attachmentsSubmittedData = this.mapPatientToAttachments(this.workingPatient);
            this.signatureAttachment = getSignatureAttachment(this.workingPatient as any, this.resolveSignatureExtensionUrl()) ?? null;
        });
    }

    async prepareFieldConfigs() {
        // Use shared builder service; leave patient-registration.component untouched
        this.designatedRegForm = { formName: 'Patient Details' } as formMetaData;
        this.designatedFormFields = await this.fieldFactory.buildPatientDetailsFields({ mode: 'edit' });
        // Removed dialogContext === 'profile' blanket filter so user can add new contacts
        
        this.guardianRegForm = { formName: 'Guardian' } as formMetaData;
        this.guardianFormFields = await this.fieldFactory.buildGuardianFields({ includeGuardianGender: false });

    }

    // Mapping helpers
    private mapPatientToDesignated(p: Patient): any {
        const name = (p.name && p.name[0]) || ({} as any);
        const telecom = p.telecom || [];
        let phone = telecom.filter(t => t.system === 'phone');
        
        if (this.dialogContext === 'profile' && phone.length > 0) {
            this.hiddenPrimaryPhone = phone[0];
            phone = phone.slice(1);
        }

        let email = telecom.filter(t => t.system === 'email');
        
        if (this.dialogContext === 'profile' && email.length > 0) {
            this.hiddenPrimaryEmail = email[0];
            email = email.slice(1);
        }

// $#$        // /* alert(JSON.stringify(email) */)
        const other = telecom.filter(t => t.system !== 'phone' && t.system !== 'email');
        return {
            active: p.active ?? true,
            name: {
                title: name.prefix?.[0] || '',
                family: name.family || '',
                given: name.given?.[0] || '',
                otherNames: name.given && name.given.length > 1 ? name.given.slice(1).join(' ') : ''
            },
            gender: p.gender ? (p.gender.charAt(0).toUpperCase() + p.gender.slice(1)) : undefined,
            birthDate: p.birthDate,
            address: p.address || [],
            phone_telecom: phone,
            email_telecom: email,
            telecom: other,
            maritalStatus: p.maritalStatus ? this.stringifyCodeableConcept(p.maritalStatus) : undefined,
        };
    }

    private mapPatientToGuardian(p: Patient): any {
        const c = (p.contact && p.contact[0]) || ({} as any);
        const telecom: any[] = c.telecom || [];
        const phone = telecom.filter(t => t.system === 'phone');
        const email = telecom.filter(t => t.system === 'email');
        return {
            name: {
                title: c.name?.prefix?.[0] || '',
                family: c.name?.family || '',
                given: c.name?.given?.[0] || '',
                otherNames: c.name?.given && c.name?.given.length > 1 ? c.name?.given.slice(1).join(' ') : ''
            },
            relationship: c.relationship?.[0] ? this.stringifyCodeableConcept(c.relationship[0]) : undefined,
            phone_telecom: phone,
            email_telecom: email,
            address: c.address ? [c.address] : []
        };
    }

    private mapPatientToAttachments(p: Patient): Attachment[] {
        return (p.photo || []).map(att => ({
            title: att.title || undefined,
            url: att.url || undefined,
            contentType: att.contentType || undefined,
            creation: att.creation || undefined,
            size: att.size
        }));
    }

    private stringifyCodeableConcept(cc: any): string | undefined {
        const c = cc?.coding?.[0];
        if (!c) return undefined;
        return `${c.code}$#$${c.display}$#$${c.system}`;
    }

    // Edit handlers
    onDetailsEdited(event: FieldEditEvent) {
        if (!this.designatedSubmittedData) return;
        if (event.isArray && event.arrayIndex !== undefined) {
            this.designatedSubmittedData[event.fieldApiName][event.arrayIndex] = event.newValue;
        } else if (event.fieldApiName.includes('.')) {
            // nested keys not expected here
        } else {
            this.designatedSubmittedData[event.fieldApiName] = event.newValue;
        }
        this.applyDetailsToPatient();
    }

    onGuardianEdited(event: FieldEditEvent) {
        if (!this.guardianSubmittedData) return;
        if (event.isArray && event.arrayIndex !== undefined) {
            this.guardianSubmittedData[event.fieldApiName][event.arrayIndex] = event.newValue;
        } else {
            this.guardianSubmittedData[event.fieldApiName] = event.newValue;
        }
        this.applyGuardianToPatient();
    }

    onAttachmentsChanged(attachments: Attachment[]) {
        console.log('📤 Attachments before update:', this.workingPatient.photo);

        this.attachmentsSubmittedData = Array.isArray(attachments) ? attachments : [];
        this.applyAttachmentsToPatient();
        console.log('📤 Attachments after update:', this.workingPatient.photo);
// $#$        // /* debugger */
    }

    onSignatureChanged(signature: Attachment | null) {
        this.signatureAttachment = signature;
        this.applySignatureToPatient();
    }

    private applyDetailsToPatient() {
        const d = this.designatedSubmittedData || {};
        const name = d.name || {};
        this.workingPatient.active = d.active ?? this.workingPatient.active;
        
        const officialName = {
            use: 'official',
            prefix: name.title ? [name.title] : undefined,
            family: name.family,
            given: [name.given, ...(name.otherNames ? [name.otherNames] : [])].filter(Boolean)
        };
        
        if (this.workingPatient.name && this.workingPatient.name.length > 0) {
            this.workingPatient.name[0] = { ...this.workingPatient.name[0], ...officialName } as any;
        } else {
            this.workingPatient.name = [officialName as any];
        }

        this.workingPatient.gender = d.gender ? (d.gender as string).toLowerCase() as any : this.workingPatient.gender;
        
        if (d.birthDate) {
            if (d.birthDate instanceof Date) {
                this.workingPatient.birthDate = d.birthDate.toISOString().split('T')[0];
            } else if (typeof d.birthDate === 'string') {
                this.workingPatient.birthDate = d.birthDate.split('T')[0];
            }
        }

        if (d.address && Array.isArray(d.address)) {
            // Merge with existing addresses instead of overwriting completely
            if (this.workingPatient.address && this.workingPatient.address.length > 0) {
                this.workingPatient.address = [...d.address, ...this.workingPatient.address.slice(d.address.length)];
            } else {
                this.workingPatient.address = d.address;
            }
        }

        // marital status
        if (d.maritalStatus) {
            this.workingPatient.maritalStatus = this.parseCodeableConcept(d.maritalStatus) || this.workingPatient.maritalStatus;
        }

        // merge telecom arrays while preserving others if needed
        const telecom: any[] = [];
        
        if (this.hiddenPrimaryPhone) {
            telecom.push({ ...this.hiddenPrimaryPhone, system: 'phone' });
        }
        if (Array.isArray(d.phone_telecom)) telecom.push(...d.phone_telecom.map((t: any) => ({ ...t, system: 'phone' })));
        
        if (this.hiddenPrimaryEmail) {
            telecom.push({ ...this.hiddenPrimaryEmail, system: 'email' });
        }
        if (Array.isArray(d.email_telecom)) telecom.push(...d.email_telecom.map((t: any) => ({ ...t, system: 'email' })));
        if (Array.isArray(d.telecom)) telecom.push(...d.telecom);
        
        // Retain original telecom entries that are not phone or email, if they were not included in the dynamic form
        const existingOtherTelecom = (this.workingPatient.telecom || []).filter(t => t.system !== 'phone' && t.system !== 'email');
        if (telecom.length === 0 && d.phone_telecom === undefined && d.email_telecom === undefined) {
            // Do nothing if telecom wasn't touched
        } else {
            // If the dynamic form touched telecom, only replace the fields the form knows about
            this.workingPatient.telecom = [...telecom, ...existingOtherTelecom];
        }
    }

    private parseCodeableConcept(value?: string): any | undefined {
        if (!value) return undefined;
        const parts = value.split('$#$');
        if (parts.length >= 3) {
            return { coding: [{ code: parts[0], display: parts[1], system: parts[2] }], text: parts[1] };
        }
        return undefined;
    }

    private applyGuardianToPatient() {
        const g = this.guardianSubmittedData || {};
        const contact: any = {};
        if (g.relationship) contact.relationship = [this.parseCodeableConcept(g.relationship)];
        if (g.name) {
            contact.name = {
                use: 'official',
                prefix: g.name.title ? [g.name.title] : undefined,
                family: g.name.family,
                given: [g.name.given, ...(g.name.otherNames ? [g.name.otherNames] : [])].filter(Boolean)
            };
        }
        const telecom: any[] = [];
        if (Array.isArray(g.phone_telecom)) telecom.push(...g.phone_telecom.map((t: any) => ({ ...t, system: 'phone' })));
        if (Array.isArray(g.email_telecom)) telecom.push(...g.email_telecom.map((t: any) => ({ ...t, system: 'email' })));
        if (telecom.length) contact.telecom = telecom;
        if (Array.isArray(g.address) && g.address[0]) contact.address = g.address[0];
        
        if (Object.keys(contact).length > 0) {
            if (this.workingPatient.contact && this.workingPatient.contact.length > 0) {
                this.workingPatient.contact[0] = { ...this.workingPatient.contact[0], ...contact };
            } else {
                this.workingPatient.contact = [contact];
            }
        }
    }

    private applyAttachmentsToPatient() {
        const attachments = Array.isArray(this.attachmentsSubmittedData) ? this.attachmentsSubmittedData : [];
        this.workingPatient.photo = attachments.map(att => ({
            title: att.title || undefined,
            url: att.url || undefined,
            contentType: att.contentType || undefined,
            creation: att.creation || undefined,
            size: att.size
        }));
    }

    private applySignatureToPatient() {
        setSignatureAttachment(this.workingPatient as any, this.resolveSignatureExtensionUrl(), this.signatureAttachment);
    }

    private resolveSignatureExtensionUrl(): string {
        return this.data?.resourceType === 'Practitioner'
            ? this.practitionerSignatureExtensionUrl
            : this.patientSignatureExtensionUrl;
    }

    save() {
        this.applySignatureToPatient();
        const cloned = structuredClone
            ? structuredClone(this.workingPatient)
            : JSON.parse(JSON.stringify(this.workingPatient));

        sanitizePatientResource(cloned);
        const pruned = pruneFhirResource(cloned);
        console.log('✅ Final pruned resource ready to save:', pruned);
// $#$        // /* debugger; */
        this.dialogRef.close({ updated: true, resource: pruned });
    }

    // Advance to next tab, or save if on the last tab
    goNextTabOrSave() {
        if (!this.tabGroup) return;
        const current = this.tabGroup.selectedIndex ?? 0;
        // Try public tabs first; fall back to private _tabs for compatibility
        const total = ((this.tabGroup as any).tabs?.length ?? (this.tabGroup as any)._tabs?.length ?? 0) as number;
        if (current < total - 1) {
            this.tabGroup.selectedIndex = current + 1;
        } else {
            this.save();
        }
    }

    // Move to previous tab if available
    goPrevTab() {
        if (!this.tabGroup) return;
        const current = this.tabGroup.selectedIndex ?? 0;
        if (current > 0) {
            this.tabGroup.selectedIndex = current - 1;
        }
    }

    // pruneFHIRArrays moved to shared utils
}

